import { Link, NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Package, ShoppingBag, Tag, Users, FileText, Palette, BookOpen, LogOut, ArrowLeft } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

const items = [
    { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
    { to: "/admin/products", label: "Products", icon: Package },
    { to: "/admin/orders", label: "Orders", icon: ShoppingBag },
    { to: "/admin/content", label: "Content", icon: Palette },
    { to: "/admin/pages", label: "Pages", icon: FileText },
    { to: "/admin/coupons", label: "Coupons", icon: Tag },
    { to: "/admin/customers", label: "Customers", icon: Users },
    { to: "/manual", label: "Manual", icon: BookOpen },
];

export default function AdminLayout({ children }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const onLogout = async () => {
        await logout();
        navigate("/");
    };

    return (
        <div className="flex min-h-screen bg-obsidian text-white" data-testid="admin-layout">
            <aside className="hidden w-64 flex-shrink-0 border-r border-border-dim bg-surface lg:block">
                <div className="border-b border-border-dim px-6 py-6">
                    <Link to="/" className="flex items-center gap-2">
                        <span className="relative inline-flex h-8 w-8 items-center justify-center rounded-full border border-gold">
                            <span className="font-display text-sm text-gold">L</span>
                            <span className="absolute -right-1 -top-1 text-heart">♥</span>
                        </span>
                        <span className="font-display text-lg tracking-[0.15em] text-gold">LOVINLIFE</span>
                    </Link>
                    <p className="mt-2 text-[10px] uppercase tracking-[0.3em] text-white/40">Atelier · admin</p>
                </div>
                <nav className="space-y-1 p-4">
                    {items.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.end}
                            data-testid={`admin-nav-${item.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                                    isActive
                                        ? "bg-gold text-obsidian"
                                        : "text-white/70 hover:bg-surface-2 hover:text-gold"
                                }`
                            }
                        >
                            <item.icon className="h-4 w-4" />
                            <span className="uppercase tracking-[0.2em]">{item.label}</span>
                        </NavLink>
                    ))}
                </nav>
                <div className="absolute bottom-6 left-4 right-4 space-y-2">
                    <Link
                        to="/"
                        className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-widest text-white/60 hover:text-gold"
                    >
                        <ArrowLeft className="h-3 w-3" /> Back to storefront
                    </Link>
                    <div className="border-t border-border-dim pt-3 px-4">
                        <p className="text-[10px] uppercase tracking-[0.25em] text-white/40">Signed in</p>
                        <p className="truncate text-sm text-white">{user?.name}</p>
                        <button
                            onClick={onLogout}
                            data-testid="admin-logout"
                            className="mt-2 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-white/60 hover:text-heart"
                        >
                            <LogOut className="h-3 w-3" /> Sign out
                        </button>
                    </div>
                </div>
            </aside>
            <main className="flex-1 overflow-auto">
                <div className="border-b border-border-dim bg-surface px-4 py-3 lg:hidden">
                    <nav className="flex gap-2 overflow-x-auto">
                        {items.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.end}
                                className={({ isActive }) =>
                                    `whitespace-nowrap border px-3 py-2 text-[10px] uppercase tracking-widest ${
                                        isActive ? "border-gold bg-gold text-obsidian" : "border-border-dim text-white/70"
                                    }`
                                }
                            >
                                {item.label}
                            </NavLink>
                        ))}
                    </nav>
                </div>
                <div className="p-4 sm:p-6 lg:p-10">{children}</div>
            </main>
        </div>
    );
}
